// EditorHeader shows the map title and description as well as controls
// in the upper-right.

import ErrorPopup from 'app/src/editor/ErrorPopup';
import { useMapStoreContext } from 'app/src/editor/MapStoreContext';
import MarkupPopup from 'app/src/editor/MarkupPopup';
import SolutionsPopup from 'app/src/editor/SolutionsPopup';
import { useUndoRedoContext } from 'app/src/editor/UndoRedoContext';
import useValueChanged from 'app/src/utils/useValueChanged';
import * as React from 'react';

type ErrorPopupDetails = {
	message: string;
	details: string;
};

export default function EditorHeader(): React.ReactElement {
	const [errorPopupDetails, setErrorPopupDetails] = React.useState<ErrorPopupDetails | null>(null);
	const [showMarkupPopup, setShowMarkupPopup] = React.useState(false);
	const [showSolutionsPopup, setShowSolutionsPopup] = React.useState(false);

	const [editingTitle, setEditingTitle] = React.useState(false);
	const [editingDescription, setEditingDescription] = React.useState(false);

	const titleInputRef = React.useRef<HTMLInputElement | null>(null);
	const descriptionInputRef = React.useRef<HTMLInputElement | null>(null);

	const { mapStore } = useMapStoreContext();

	const { addUndoState } = useUndoRedoContext();

	const finishEditingTitle = (): void => {
		const titleText = titleInputRef.current ? titleInputRef.current.value : '';

		if (titleText && titleText !== mapStore.title) {
			mapStore.setTitle(titleText);
			addUndoState();
		}

		setEditingTitle(false);
	};

	const finishEditingDescription = (): void => {
		const descriptionText = descriptionInputRef.current ? descriptionInputRef.current.value : '';

		if (descriptionText && descriptionText !== mapStore.description) {
			mapStore.setDescription(descriptionText);
			addUndoState();
		}

		setEditingDescription(false);
	};

	const handleTitleKeypress = (e: React.KeyboardEvent<HTMLInputElement>): void => {
		if (e.which === 13 /* return */) {
			finishEditingTitle();
		}
	};

	const handleDescriptionKeypress = (e: React.KeyboardEvent<HTMLInputElement>): void => {
		if (e.which === 13 /* return */) {
			finishEditingDescription();
		}
	};

	const editingTitleChanged = useValueChanged(editingTitle);
	const editingDescriptionChanged = useValueChanged(editingDescription);
	React.useEffect(() => {
		if (editingTitleChanged && editingTitle && titleInputRef.current) {
			titleInputRef.current.focus();
		} else if (editingDescriptionChanged && editingDescription && descriptionInputRef.current) {
			descriptionInputRef.current.focus();
		}
	}, [editingTitleChanged, editingDescriptionChanged, editingTitle, editingDescription]);

	let titleElement: JSX.Element;
	if (editingTitle) {
		titleElement = (
			<input
				type="text"
				defaultValue={mapStore.title}
				onKeyPress={handleTitleKeypress}
				onBlur={(): void => {
					finishEditingTitle();
				}}
				className="EditorHeader-titleInput"
				ref={titleInputRef}
			/>
		);
	} else {
		titleElement = (
			<h1 className="EditorHeader-title" onClick={(): void => setEditingTitle(true)}>
				{mapStore.title}
			</h1>
		);
	}

	let descriptionElement: JSX.Element;
	if (editingDescription) {
		descriptionElement = (
			<input
				type="text"
				defaultValue={mapStore.description}
				onKeyPress={handleDescriptionKeypress}
				onBlur={(): void => {
					finishEditingDescription();
				}}
				className="EditorHeader-descriptionInput"
				ref={descriptionInputRef}
			/>
		);
	} else {
		descriptionElement = (
			<p className="EditorHeader-description" onClick={(): void => setEditingDescription(true)}>
				{mapStore.description}
			</p>
		);
	}

	return (
		<React.Fragment>
			<div className="EditorHeader">
				<div className="EditorHeader-titleContainer">
					{titleElement}
					{descriptionElement}
				</div>
				<div className="EditorHeader-controls">
					<button
						className="EditorHeader-control EditorHeader-control--new"
						onClick={(): void => mapStore.reset()}
						title="Reset Map"
					>
						New
					</button>
					<button
						className="EditorHeader-control EditorHeader-control--solve"
						onClick={(): void => setShowSolutionsPopup(true)}
						title="Solve Level"
					>
						Solve
					</button>
					<button
						className="EditorHeader-control EditorHeader-control--viewMarkup"
						onClick={(): void => setShowMarkupPopup(true)}
						title="View/Set JSON"
					>
						Edit Markup
					</button>
				</div>
			</div>
			{showMarkupPopup && (
				<MarkupPopup
					onClose={(): void => {
						setShowMarkupPopup(false);
					}}
					onError={(message: string, details: string): void => {
						setShowMarkupPopup(false);
						setErrorPopupDetails({
							message,
							details,
						});
					}}
				/>
			)}
			{showSolutionsPopup && (
				<SolutionsPopup
					onClose={(): void => {
						setShowSolutionsPopup(false);
					}}
					onError={(message: string, details: string): void => {
						setShowSolutionsPopup(false);
						setErrorPopupDetails({
							message,
							details,
						});
					}}
				/>
			)}
			{errorPopupDetails && (
				<ErrorPopup
					errorMessage={errorPopupDetails.message}
					errorDetails={errorPopupDetails.details}
					onClose={(): void => setErrorPopupDetails(null)}
				/>
			)}
		</React.Fragment>
	);
}
